import { getDeviceInfo } from './deviceInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const seen = new Set();
let reportCount = 0;
const MAX_REPORTS_PER_SESSION = 15;
const IGNORED = [
  'ResizeObserver loop',
  // Meta's FB/IG in-app browser injects its own logger (iabjs://) that throws when the
  // user leaves the page — third-party noise, not an app error.
  'Java object is gone',
  // Meta's iOS FB/IG in-app browser injects a script that calls
  // `window.webkit.messageHandlers.<name>.postMessage(...)`, but on some Meta client
  // builds `messageHandlers` is undefined and the injected script throws. Signature
  // seen in production reports: minified stack `f@:1:681 → w@:1:1849 → @:1:2340`,
  // always with a URL containing `fbclid=`. Not our code — third-party noise.
  "evaluating 'window.webkit.messageHandlers'",
  'window.webkit.messageHandlers',
  // Opaque cross-origin errors from third-party scripts (our bundle is same-origin).
  'Script error.',
];
// Schemes used by scripts injected into our page by in-app browsers / extensions.
const THIRD_PARTY_SCRIPT = /^(iabjs|gap|chrome-extension|moz-extension|safari-extension|safari-web-extension|ms-browser-extension):/i;
// Background/polling endpoints whose transient network blips are noise (not real failures)
const BACKGROUND_ENDPOINTS = [
  '/notifications', '/messages/typing', '/analytics/track', '/analytics/locate',
  '/theme/debug', '/system/status', '/auth/me',
];

// --- iOS PWA suspend-resume tracker -----------------------------------------
// iOS aggressively kills in-flight fetches when a PWA is backgrounded, locked,
// or rotated. The user sees "Network Error" for a request that was legit \u2014
// the OS just yanked the connection. We track the last time the page went
// hidden so the interceptor can distinguish "OS killed my request" (retry
// silently on return) from "the server is genuinely unreachable" (report it).
let lastHiddenAt = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') lastHiddenAt = Date.now();
  });
  // Safari fires pageshow (persisted=true) when returning from the bfcache \u2014
  // treat that as a suspend-resume too.
  window.addEventListener('pageshow', (e) => { if (e.persisted) lastHiddenAt = Date.now() - 100; });
}

/**
 * Wait for the page to become visible again, then resolve. If it never went
 * hidden, resolve immediately. Bounded to 30s so a truly dead request still
 * surfaces an error eventually.
 */
function waitForVisible(timeoutMs = 30000) {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      return resolve();
    }
    const done = () => {
      document.removeEventListener('visibilitychange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => { if (document.visibilityState === 'visible') done(); };
    document.addEventListener('visibilitychange', check);
    const timer = setTimeout(done, timeoutMs);
  });
}

export function reportAppError(source, message, context = {}, stack = '') {
  try {
    if (!message) return;
    if (IGNORED.some((p) => String(message).includes(p))) return;
    const sig = `${source}|${String(message).slice(0, 200)}`;
    if (seen.has(sig) || reportCount >= MAX_REPORTS_PER_SESSION) return;
    seen.add(sig);
    reportCount += 1;
    const payload = JSON.stringify({
      source,
      message: String(message).slice(0, 2000),
      stack: String(stack || '').slice(0, 5000),
      device_info: getDeviceInfo(),
      context: { url: window.location.href.slice(0, 500), ...context },
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/log/client-error`, new Blob([payload], { type: 'application/json' }));
    } else {
      fetch(`${API}/log/client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[reportAppError] suppressed', e); }
}

export function setupGlobalErrorLogging(axiosInstance) {
  // Uncaught JS runtime errors
  window.addEventListener('error', (e) => {
    if (THIRD_PARTY_SCRIPT.test(String(e.filename || ''))) return;
    reportAppError('js_error', e.message, { file: `${e.filename || ''}:${e.lineno || 0}:${e.colno || 0}` }, e.error?.stack);
  });

  // Unhandled promise rejections (axios errors are covered by the interceptor below)
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    if (r?.isAxiosError) return;
    reportAppError('unhandled_rejection', r?.message || String(r), {}, r?.stack);
  });

  // Failed API calls: server 5xx errors + unexpected network failures while online.
  // Also silently retries iOS-PWA-suspend network drops on GETs so users don't see
  // spinners for what was really the OS killing their connection.
  axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      try {
        const cfg = error?.config || {};
        const url = String(cfg.url || '');
        const isReportingEndpoint = url.includes('/log/client-error') || url.includes('/auth/report-login-issue');
        const isCanceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.message === 'canceled';
        const status = error?.response?.status;
        const method = (cfg.method || 'get').toUpperCase();
        const isNetworkError = !error?.response && navigator.onLine && !isCanceled;

        // --- iOS PWA suspend-resume silent retry --------------------------------
        // If a GET failed with no response and the page went hidden within the last
        // 30 seconds (or is hidden right now), it's almost certainly the OS killing
        // the socket, not a real server problem. Wait for the page to be visible
        // again and retry once \u2014 the user never sees the flake and no false
        // "Network Error" is logged.
        const wentHiddenRecently = Date.now() - lastHiddenAt < 30000 || document.visibilityState === 'hidden';
        if (isNetworkError && method === 'GET' && !cfg.__pwaRetried && wentHiddenRecently && !isReportingEndpoint) {
          cfg.__pwaRetried = true;
          await waitForVisible();
          try {
            return await axiosInstance.request(cfg);
          } catch (retryErr) {
            error = retryErr;  // fall through to normal error reporting below
          }
        }

        if (!isReportingEndpoint && !isCanceled) {
          if (status >= 500) {
            reportAppError('api_5xx', `${method} ${url} -> ${status}: ${JSON.stringify(error.response?.data || '').slice(0, 300)}`);
          } else if (!error?.response && navigator.onLine) {
            const isBackground = BACKGROUND_ENDPOINTS.some((p) => url.includes(p));
            if (!isBackground) {
              reportAppError('api_network_error', `${method} ${url} -> ${error?.message || 'network error'}`);
            }
          }
        }
      } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('[error-interceptor] suppressed', e); }
      return Promise.reject(error);
    }
  );
}
